// frontend/src/portal/PortalApp.jsx
import React from "react";
import { Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import { useAuth, AuthenticateWithRedirectCallback } from "@clerk/clerk-react";

import RequireClient from "./components/RequireClient.jsx";

import PortalOverview from "./pages/PortalOverview.jsx";
import PortalCompany from "./pages/PortalCompany.jsx";
import PortalInviteUsers from "./pages/PortalInviteUsers.jsx";
import PortalTenantMembers from "./pages/PortalTenantMembers.jsx";
import SuperAdminDashboard from "./pages/SuperAdminDashboard.jsx";

import OpsTenants from "../pages/OpsTenants.jsx";
import OpsAuditLog from "../pages/OpsAuditLog.jsx";
import OpsTenantMembers from "./pages/OpsTenantMembers.jsx";

import AdminTenantBranding from "./pages/AdminTenantBranding.jsx";

import CustomSignIn from "./components/CustomSignIn.jsx";
import PortalForgotPassword from "./pages/PortalForgotPassword.jsx";
import ContextHelpPanel from "../components/ContextHelpPanel.jsx";

/**
 * Google/AWS-style hardening:
 * - Sanitize returnUrl (prevent open redirects)
 * - Keep auth routes outside RequireClient
 * - Avoid duplicate returnUrl vars
 * - Default to a safe signed-in landing page
 */
function getSafeReturnUrl(search, fallback = "/portal/overview") {
  const params = new URLSearchParams(search);
  const raw = params.get("returnUrl");

  if (!raw) return fallback;

  // Only allow in-app portal routes (blocks http(s)://, //evil.com, /api, etc.)
  // You can tighten this further to allow only a small set of known routes.
  if (raw.startsWith("/portal/")) return raw;

  return fallback;
}

function PortalLogin() {
  const location = useLocation();
  const navigate = useNavigate();
  const { isLoaded, isSignedIn } = useAuth();

  const safeReturnUrl = React.useMemo(() => getSafeReturnUrl(location.search, "/portal/overview"), [location.search]);

  // Redirect authenticated users away from the login page
  React.useEffect(() => {
    if (isLoaded && isSignedIn) {
      navigate(safeReturnUrl, { replace: true });
    }
  }, [isLoaded, isSignedIn, safeReturnUrl, navigate]);

  // Show loading while checking auth
  if (!isLoaded) {
    return (
      <div className="portal-main">
        <div className="portal-loading">
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  // Already signed in - let the effect redirect
  if (isSignedIn) return null;

  // Not signed in - show custom login form
  return <CustomSignIn returnUrl={safeReturnUrl} />;
}

export default function PortalApp() {
  return (
    <>
      <Routes>
      {/* Public auth routes (never behind RequireClient) */}
      <Route path="login/*" element={<PortalLogin />} />
      <Route path="forgot-password/*" element={<PortalForgotPassword />} />
      <Route path="sso-callback" element={<AuthenticateWithRedirectCallback />} />

      {/* Protected app routes */}
      <Route element={<RequireClient />}>
        <Route index element={<Navigate to="overview" replace />} />
        <Route path="overview" element={<PortalOverview />} />
        <Route path="company/:id" element={<PortalCompany />} />

        {/* Admin routes */}
        <Route path="admin/dashboard" element={<SuperAdminDashboard />} />
        <Route path="admin/invite" element={<PortalInviteUsers />} />
        <Route path="admin/members" element={<PortalTenantMembers />} />
        <Route path="admin/branding" element={<AdminTenantBranding />} />

        {/* Ops routes (super admin only) */}
        <Route path="ops/tenants" element={<OpsTenants />} />
        <Route path="ops/tenants/:tenantId/members" element={<OpsTenantMembers />} />
        <Route path="ops/audit" element={<OpsAuditLog />} />
      </Route>

      {/* Catch-all: redirect unknown routes to overview */}
        <Route path="*" element={<Navigate to="overview" replace />} />
      </Routes>
      <ContextHelpPanel />
    </>
  );
}
